import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Loader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from 'components';
import _ from 'lodash';

import { useSelector, useTranslation } from 'third-party';

import { USER_INPUT_DEBOUNCE_TIME_MS } from 'constants/common';

import { useSettings } from 'modules/setting';

import { appSelectors, settingSelectors } from 'selectors';

import { emailRegexp } from 'utils/validations';

import { APISettingName } from 'types/swaggerApi';

export const GeneralSettings = () => {
  const generalSettings = useSelector(settingSelectors.generalSettings);
  const loadSettingsPending = useSelector(settingSelectors.loadSettingsPending);
  const currentTenant = useSelector(appSelectors.currentTenant);
  const eCheckPrintLimitValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.ECheckPrintLimit, '1'),
  );
  const redirectEmailValue = useSelector(
    settingSelectors.getSettingValue(APISettingName.RedirectEmail, ''),
  );
  const timezoneValue = useSelector(
    settingSelectors.getSettingValue(
      APISettingName.Timezone,
      'America/New_York',
    ),
  );
  const ref = useRef<HTMLInputElement>(null);
  const [emailError, setEmailError] = useState<string>('');
  const [timezone, setTimezone] = useState<string>('');
  const [redirectEmail, setRedirectEmail] = useState<string>('');
  const { t } = useTranslation();
  const { loadSettings, saveSetting } = useSettings();

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (ref.current && generalSettings.length > 0) {
      ref.current.value = eCheckPrintLimitValue;
    }
    if (generalSettings.length > 0) {
      setRedirectEmail(redirectEmailValue);
      setTimezone(timezoneValue);
    }
  }, [
    generalSettings,
    eCheckPrintLimitValue,
    redirectEmailValue,
    timezoneValue,
  ]);

  const validateEmail = (email: string): boolean => {
    if (!email) {
      setEmailError('');
      return true;
    }
    const isValid = emailRegexp.test(email) && email.length <= 254;
    setEmailError(
      isValid ? '' : t('settings.generalSettings.RedirectEmail.error'),
    );
    return isValid;
  };

  const handleSave = _.debounce((name: APISettingName, value: string) => {
    saveSetting({
      name: name,
      value: value,
    });
  }, USER_INPUT_DEBOUNCE_TIME_MS);

  const handleEmailChange = (value: string) => {
    setRedirectEmail(value);
    if (validateEmail(value)) {
      handleSave(APISettingName.RedirectEmail, value);
    }
  };

  const handleTimezoneChange = (value: string) => {
    setTimezone(value);
    saveSetting({
      name: APISettingName.Timezone,
      value: value,
    });
  };

  if (loadSettingsPending) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} alignItems="center">
          <>
            <TextField
              label={t('settings.generalSettings.ECheckPrintLimit')}
              type="number"
              inputRef={ref}
              variant="standard"
              onChange={e =>
                handleSave(APISettingName.ECheckPrintLimit, e.target.value)
              }
              sx={{ minWidth: 200 }}
              slotProps={{
                htmlInput: {
                  min: 1,
                  max: 100,
                },
              }}
            />
            <Typography variant="body2">
              {t('settings.generalSettings.ECheckPrintLimit.label')}
            </Typography>
          </>
        </Stack>

        {currentTenant?.isSandbox && (
          <Stack direction="row" spacing={2} alignItems="center">
            <>
              <TextField
                label={t('settings.generalSettings.RedirectEmail')}
                type="email"
                value={redirectEmail}
                variant="standard"
                onChange={e => handleEmailChange(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                sx={{ minWidth: 200 }}
              />
              <Typography variant="body2">
                {t('settings.generalSettings.RedirectEmail.label')}
              </Typography>
            </>
          </Stack>
        )}

        <Stack direction="row" spacing={2} alignItems="center">
          <Select
            label="Timezone"
            value={timezone}
            onChange={e => handleTimezoneChange(e.target.value)}
            variant="standard"
            sx={{ minWidth: 200 }}
          >
            {(Intl as any)
              .supportedValuesOf('timeZone')
              .filter((it: string) => it.startsWith('America/'))
              .map((it: string) => (
                <MenuItem key={it} value={it}>
                  {it}
                </MenuItem>
              ))}
          </Select>
          <Typography variant="body2">Timezone</Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
